import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { OrganizationCustomerDTO } from '../../../dto/organization/organization-customer.dto';
import { normalizeSpreadsheetNumericString } from '../../../utils/customer-csv.util';
import { bindDanishPostalCodeCityLookup } from '../../../utils/dk-postal-code.util';
import {
  applyCustomerCvrLookup,
  fetchCustomerByCvr,
  normalizeDanishCvr,
} from '../../../utils/customer-cvr-lookup.util';

interface PriceGroupOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-duna-customers-edit-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
  ],
  template: `
    <h2 mat-dialog-title>Rediger kunde</h2>
    <mat-dialog-content>
      @if (createdAtLabel) {
      <p class="created-at">{{ createdAtLabel }}</p>
      }
      <form [formGroup]="editForm" class="edit-form">
        <div class="field-row">
          <mat-form-field appearance="outline" class="cvr-field">
            <mat-label>CVR</mat-label>
            <input matInput formControlName="cvr" />
            <button
              mat-icon-button
              matSuffix
              type="button"
              aria-label="Hent oplysninger fra CVR"
              matTooltip="Hent fra CVR"
              [disabled]="!canFetchCvr || cvrLookupLoading"
              (click)="fetchFromCvr()"
            >
              @if (cvrLookupLoading) {
              <mat-progress-spinner
                diameter="20"
                mode="indeterminate"
              ></mat-progress-spinner>
              } @else {
              <mat-icon>download</mat-icon>
              }
            </button>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>EAN</mat-label>
            <input matInput formControlName="ean" />
          </mat-form-field>
        </div>
        @if (cvrLookupHint) {
        <p class="cvr-hint" [class.cvr-hint--error]="cvrLookupError">
          {{ cvrLookupHint }}
        </p>
        }

        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Navn *</mat-label>
          <input matInput formControlName="name" />
          @if (editForm.get('name')?.hasError('required')) {
          <mat-error>Navn er påkrævet</mat-error>
          }
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Att.</mat-label>
          <input matInput formControlName="att" />
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>E-mail</mat-label>
          <input matInput formControlName="email" type="email" />
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Telefonnummer</mat-label>
          <input matInput formControlName="phone" />
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Mobilnummer</mat-label>
          <input matInput formControlName="mobile" />
        </mat-form-field>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Adresse</mat-label>
          <input matInput formControlName="address" />
        </mat-form-field>
        <div class="field-row">
          <mat-form-field appearance="outline">
            <mat-label>Postnummer</mat-label>
            <input matInput formControlName="postal_code" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>By</mat-label>
            <input matInput formControlName="city" />
          </mat-form-field>
        </div>
        <mat-form-field appearance="outline" style="width:100%">
          <mat-label>Prisgruppe</mat-label>
          <mat-select
            formControlName="price_group_id"
            [compareWith]="comparePriceGroupIds"
          >
            <mat-option [value]="0">Ingen</mat-option>
            @for (g of priceGroups; track g) {
            <mat-option [value]="g.id">{{ g.label }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (priceGroupsHint) {
        <p class="price-groups-hint">{{ priceGroupsHint }}</p>
        } @if (error) {
        <p style="color:red">{{ error }}</p>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-stroked-button
        (click)="dialogRef.close(false)"
        [disabled]="isSubmitting"
      >
        Annuller
      </button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="editForm.invalid || isSubmitting"
        (click)="onSave()"
      >
        @if (isSubmitting) {
        <mat-progress-spinner
          diameter="18"
          mode="indeterminate"
          style="display:inline-block;margin-right:6px"
        ></mat-progress-spinner>
        } Gem
      </button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .edit-form {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-top: 8px;
        min-width: 420px;
      }
      .field-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .price-groups-hint {
        margin: 0;
        font-size: 0.85rem;
        color: #666;
      }
      .created-at {
        margin: 0 0 4px;
        font-size: 0.85rem;
        color: rgba(0, 0, 0, 0.55);
      }
      .cvr-hint {
        margin: -6px 0 2px;
        font-size: 0.85rem;
        color: #2e7d32;
      }
      .cvr-hint--error {
        color: #c62828;
      }
    `,
  ],
})
export class DunaCustomersEditDialogComponent implements OnInit, OnDestroy {
  editForm: FormGroup;
  isSubmitting = false;
  error: string | null = null;
  priceGroupsHint: string | null = null;
  priceGroups: PriceGroupOption[] = [];
  createdAtLabel: string | null = null;
  cvrLookupLoading = false;
  cvrLookupHint: string | null = null;
  cvrLookupError = false;
  private postalCodeLookupSub?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<DunaCustomersEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { customer: OrganizationCustomerDTO },
    private fb: FormBuilder,
    private http: HttpClient
  ) {
    this.editForm = this.fb.group({
      name: [data.customer.name, [Validators.required]],
      att: [data.customer.att ?? ''],
      email: [data.customer.email ?? ''],
      phone: [data.customer.phone ?? ''],
      mobile: [data.customer.mobile ?? ''],
      address: [data.customer.address ?? ''],
      postal_code: [data.customer.postal_code ?? ''],
      city: [data.customer.city ?? ''],
      ean: [
        normalizeSpreadsheetNumericString(data.customer.ean ?? undefined) ??
          data.customer.ean ??
          '',
      ],
      cvr: [
        normalizeSpreadsheetNumericString(data.customer.cvr ?? undefined) ??
          data.customer.cvr ??
          '',
      ],
      price_group_id: [this.toFormPriceGroupId(data.customer.price_group_id)],
    });
  }

  comparePriceGroupIds = (
    a: number | null | undefined,
    b: number | null | undefined
  ): boolean => {
    const left = this.normalizePriceGroupId(a);
    const right = this.normalizePriceGroupId(b);
    return left === right;
  };

  private toFormPriceGroupId(value: unknown): number {
    return this.normalizePriceGroupId(value) ?? 0;
  }

  private normalizePriceGroupId(value: unknown): number | null {
    if (value === null || value === undefined || value === '' || value === 0) {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : null;
  }

  ngOnInit(): void {
    this.createdAtLabel = this.formatCreatedAt(this.data.customer.created_at);
    this.loadPriceGroups();
    this.postalCodeLookupSub = bindDanishPostalCodeCityLookup(
      this.editForm,
      this.http
    );
  }

  ngOnDestroy(): void {
    this.postalCodeLookupSub?.unsubscribe();
  }

  get canFetchCvr(): boolean {
    return normalizeDanishCvr(this.editForm.get('cvr')?.value) !== null;
  }

  fetchFromCvr(): void {
    const cvr = normalizeDanishCvr(this.editForm.get('cvr')?.value);
    if (!cvr || this.cvrLookupLoading) {
      return;
    }

    this.cvrLookupLoading = true;
    this.cvrLookupHint = null;
    this.cvrLookupError = false;

    fetchCustomerByCvr(this.http, environment.apiUrl, cvr).subscribe({
      next: (data) => {
        this.cvrLookupLoading = false;
        const applied = applyCustomerCvrLookup(this.editForm, data);
        if (applied) {
          this.cvrLookupHint = 'Oplysninger hentet fra CVR.';
        } else {
          this.cvrLookupError = true;
          this.cvrLookupHint =
            'Virksomheden blev fundet, men der var ingen adresseoplysninger.';
        }
      },
      error: (err) => {
        this.cvrLookupLoading = false;
        this.cvrLookupError = true;
        this.cvrLookupHint =
          err?.error?.message || 'Kunne ikke hente oplysninger fra CVR.';
      },
    });
  }

  private formatCreatedAt(value: string | null | undefined): string | null {
    if (!value?.trim()) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return `Oprettet: ${value.trim()}`;
    return `Oprettet: ${date.toLocaleString('da-DK', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })}`;
  }

  private loadPriceGroups(): void {
    this.http
      .get<{ items?: PriceGroupOption[] }>(
        `${environment.apiUrl}/events/price-groups`,
        {
          params: { activeOnly: 'true' },
        }
      )
      .subscribe({
        next: (res) => {
          this.priceGroups = this.buildPriceGroupOptions(res?.items ?? []);
        },
        error: () => {
          this.priceGroups = this.buildPriceGroupOptions([]);
          this.priceGroupsHint = 'Kunne ikke hente prisgrupper.';
        },
      });
  }

  private buildPriceGroupOptions(
    items: PriceGroupOption[]
  ): PriceGroupOption[] {
    const groups = items
      .map((g) => ({ id: Number(g.id), label: String(g.label ?? '') }))
      .filter((g) => Number.isFinite(g.id) && g.id > 0 && g.label !== '');

    const currentId = this.normalizePriceGroupId(
      this.data.customer.price_group_id
    );
    const currentLabel = this.data.customer.price_group_label?.trim();
    if (currentId && currentLabel && !groups.some((g) => g.id === currentId)) {
      groups.unshift({ id: currentId, label: currentLabel });
    }

    if (groups.length === 0) {
      this.priceGroupsHint =
        'Der er ingen prisgrupper oprettet for organisationen.';
    } else {
      this.priceGroupsHint = null;
    }

    return groups;
  }

  onSave(): void {
    if (this.editForm.invalid || this.isSubmitting) return;
    this.isSubmitting = true;
    this.error = null;

    const payload: any = {
      name: this.editForm.value.name.trim(),
      att: this.editForm.value.att?.trim() || null,
      email: this.editForm.value.email?.trim() || null,
      phone: this.editForm.value.phone?.trim() || null,
      mobile: this.editForm.value.mobile?.trim() || null,
      address: this.editForm.value.address?.trim() || null,
      postal_code: this.editForm.value.postal_code?.trim() || null,
      city: this.editForm.value.city?.trim() || null,
      ean:
        normalizeSpreadsheetNumericString(
          this.editForm.value.ean?.trim() || undefined
        ) ?? null,
      cvr:
        normalizeSpreadsheetNumericString(
          this.editForm.value.cvr?.trim() || undefined
        ) ?? null,
      price_group_id: this.normalizePriceGroupId(
        this.editForm.value.price_group_id
      ),
    };

    this.http
      .patch(
        `${environment.apiUrl}/organizations/customers/${this.data.customer.guid}`,
        payload
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err) => {
          this.isSubmitting = false;
          this.error = err?.error?.message || 'Der opstod en fejl.';
        },
      });
  }
}
